import React from "react";

import SvgCheckCircle from "@uui/CheckCircle";
import SvgXClose from "@uui/XClose";
import { DateTime } from "luxon";
import { useNavigate } from "react-router";

import { IconButton, Typography } from "@mui/material";

import TrialCountdownChip from "@app/analytics/components/TrialCountdownChip";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import ROUTES from "@app/common/routes";
import Config from "@app/config";

import ConfirmationModal from "@rui/ConfirmationModal";
import Stack, { Row } from "@rui/Stack";

const UPGRADE_FEATURES = [
  "Workspace & team analytics",
  "Unlimited Workspace seats",
  "Full Meeting Report access",
  "Audio & video playback",
];

const FREE_TRIAL_MODAL_DAY_TIMEOUT = 1;

const FreeTrialModalItem = ({ title }: { title: string }) => {
  return (
    <Row alignItems="center" spacing={1}>
      <SvgCheckCircle sx={{ color: "shades.positive.500" }} />
      <Typography variant="bodyPrimaryReg">{title}</Typography>
    </Row>
  );
};

const FreeTrialModal = () => {
  const navigate = useNavigate();
  const { isTrial, trialDaysRemaining } = useBillingContext();

  const [freeTrialModalLastShown, setFreeTrialModalLastShown] = useLocalStorage(
    "freeTrialModalLastShown",
    DateTime.now().minus({ days: FREE_TRIAL_MODAL_DAY_TIMEOUT }).toISO() // Minus X days to show the alert initially
  );

  const handleUpgradePlanFreeTrialModal = () => {
    setFreeTrialModalLastShown(DateTime.now().toISO());

    navigate(ROUTES.UPGRADE_ACCOUNT);
  };

  const handleDismissFreeTrialModal = () => {
    setFreeTrialModalLastShown(DateTime.now().toISO());
  };

  const showFreeTrialModal =
    isTrial &&
    trialDaysRemaining <= Config.billing.trialDays / 2 &&
    (!freeTrialModalLastShown ||
      DateTime.now().diff(DateTime.fromISO(freeTrialModalLastShown)).days >=
        FREE_TRIAL_MODAL_DAY_TIMEOUT);

  return (
    <ConfirmationModal
      confirmProps={{ label: "Upgrade Now" }}
      cancelProps={{ label: "Dismiss" }}
      onClose={handleDismissFreeTrialModal}
      open={showFreeTrialModal}
      onConfirm={handleUpgradePlanFreeTrialModal}
    >
      <Stack spacing={1.5} sx={{ color: "shades.neutral.700" }}>
        <Row variant="apart">
          <TrialCountdownChip daysRemaining={trialDaysRemaining} />
          <IconButton onClick={handleDismissFreeTrialModal}>
            <SvgXClose />
          </IconButton>
        </Row>
        <Typography variant="h2">
          Upgrade to keep enjoying Read Enterprise features
        </Typography>
        <Typography variant="bodyPrimaryReg">
          Your Read free trial ends soon! Upgrade your account to keep using
          Enterprise features such as:
        </Typography>
        <Stack spacing={1}>
          {UPGRADE_FEATURES.map((feature) => (
            <FreeTrialModalItem key={feature} title={feature} />
          ))}
        </Stack>
        <Typography variant="bodyPrimaryReg">+ many more features!</Typography>
      </Stack>
    </ConfirmationModal>
  );
};

export default FreeTrialModal;
